<template>
  <DashboardDetailLayout
    title="App Usage"
    subtitle="Page visits and back-end request volume."
    :sampleData="false"
  >
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1 mb-4">
      <MetricCard
        title="Active Users (7d)"
        :value="loadingSummary ? '—' : (summary.active_users_7d_count ?? 0)"
        subtitle="Distinct users with page visits"
      />
      <MetricCard
        title="Page Visits (30d)"
        :value="loadingSummary ? '—' : (summary.page_visits_30d_count ?? 0)"
        subtitle="Total navigations logged"
      />
      <MetricCard
        title="API Calls (24h)"
        :value="loadingSummary ? '—' : (summary.api_calls_24h_count ?? 0)"
        subtitle="Back-end requests, last 24 hours"
      />
      <MetricCard
        title="Top Page (30d)"
        :value="loadingSummary ? '—' : (summary.top_page_30d_count ?? 0)"
        :subtitle="summary.top_page_30d_name || 'Most-visited page'"
      />
      <MetricCard
        title="Top User (30d)"
        :value="loadingSummary ? '—' : (summary.top_user_30d_count ?? 0)"
        :subtitle="summary.top_user_30d_name || 'User with most page visits'"
      />
    </div>

    <div class="rounded-md border-2 border-gray-300 bg-neutral-50 shadow p-3 m-2">
      <div class="flex flex-row items-center justify-between mb-2">
        <div class="text-sm tracking-widest text-gray-700 font-poppins uppercase">
          Hour of Day by Page — Last 30 Days
        </div>
        <div class="text-xs text-gray-500">
          <span v-if="loadingDetail">Loading…</span>
          <span v-else-if="detailErrorMsg" class="text-rose-600">{{ detailErrorMsg }}</span>
          <span v-else-if="detail.timezone">{{ detail.timezone }}</span>
        </div>
      </div>
      <div class="h-72">
        <Bar :data="hourChartData" :options="hourChartOptions" />
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-1 mb-2">
      <div class="rounded-md border-2 border-gray-300 bg-neutral-50 shadow p-3 m-2">
        <div class="text-sm tracking-widest text-gray-700 font-poppins uppercase mb-2">
          Top 20 Most-Visited Pages (30d)
        </div>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-gray-500 border-b">
              <th class="py-1">Page</th>
              <th class="py-1 text-right">Visits</th>
              <th class="py-1">Trend</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!loadingDetail && detail.top_pages.length === 0">
              <td colspan="3" class="py-4 text-center text-gray-500 italic">No page visits in the last 30 days.</td>
            </tr>
            <tr v-for="p in detail.top_pages" :key="'top-' + p.name" class="border-b last:border-0">
              <td class="py-1">{{ p.name }}</td>
              <td class="py-1 text-right font-mono">{{ formatNumbersWithCommas(p.total) }}</td>
              <td class="py-1"><SparklineChart :values="p.daily_series" /></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="rounded-md border-2 border-gray-300 bg-neutral-50 shadow p-3 m-2">
        <div class="text-sm tracking-widest text-gray-700 font-poppins uppercase mb-2">
          Bottom 20 Least-Visited Pages (30d)
        </div>
        <div class="text-xs text-gray-500 mb-2 italic">
          Pages that have at least one logged visit in the last 30 days. Pages with zero visits don't appear in the log.
        </div>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-gray-500 border-b">
              <th class="py-1">Page</th>
              <th class="py-1 text-right">Visits</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!loadingDetail && detail.bottom_pages.length === 0">
              <td colspan="2" class="py-4 text-center text-gray-500 italic">No data.</td>
            </tr>
            <tr v-for="p in detail.bottom_pages" :key="'bot-' + p.name" class="border-b last:border-0">
              <td class="py-1">{{ p.name }}</td>
              <td class="py-1 text-right font-mono">{{ formatNumbersWithCommas(p.total) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="rounded-md border-2 border-gray-300 bg-neutral-50 shadow p-3 m-2">
      <div class="flex flex-row items-center justify-between mb-2">
        <div class="text-sm tracking-widest text-gray-700 font-poppins uppercase">
          Per-User Activity (30d)
        </div>
        <div class="text-xs text-gray-500">
          <span v-if="loadingDetail">Loading…</span>
          <span v-else>{{ detail.users.length }} active user<span v-if="detail.users.length !== 1">s</span></span>
        </div>
      </div>
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-gray-500 border-b">
            <th class="py-1">User</th>
            <th class="py-1 text-right">Visits</th>
            <th class="py-1">Favorite Page</th>
            <th class="py-1 text-right">% of Their Visits</th>
            <th class="py-1">Last Visit</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!loadingDetail && detail.users.length === 0">
            <td colspan="5" class="py-4 text-center text-gray-500 italic">No active users in the last 30 days.</td>
          </tr>
          <tr v-for="u in detail.users" :key="u.user_id" class="border-b last:border-0">
            <td class="py-1">
              <div>{{ u.full_name }}</div>
              <div class="text-xs text-gray-500 font-mono">{{ u.username }}</div>
            </td>
            <td class="py-1 text-right font-mono">{{ formatNumbersWithCommas(u.total_visits) }}</td>
            <td class="py-1">
              {{ u.favorite_page_name }}
              <span class="text-xs text-gray-500">({{ formatNumbersWithCommas(u.favorite_page_count) }})</span>
            </td>
            <td class="py-1 text-right font-mono">{{ u.favorite_page_percent.toFixed(1) }}%</td>
            <td class="py-1 text-xs text-gray-700">{{ formatTimestamp(u.last_visit_at) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </DashboardDetailLayout>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'vue-chartjs'
import DashboardDetailLayout from '../DashboardDetailLayout.vue'
import MetricCard from '../MetricCard.vue'
import SparklineChart from '../SparklineChart.vue'
import { formatNumbersWithCommas } from '@/composables/FormatNumbers'
import { getAdminDashboardData, getAdminDashboardAppUsageDetail } from '@/services/Company'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const store = useStore()
const loadingSummary = ref(true)
const loadingDetail = ref(true)
const detailErrorMsg = ref('')
const summary = ref({})
const detail = ref({ hour_of_day_breakdown: [], timezone: '', top_pages: [], bottom_pages: [], users: [] })

const HOUR_BAR_COLORS = [
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // amber
  '#8B5CF6', // purple
  '#EF4444', // red
  '#14B8A6', // teal
  '#EC4899', // pink
  '#9CA3AF', // gray (Other)
]

function formatTimestamp(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

const hourChartData = computed(() => ({
  labels: Array.from({ length: 24 }, (_, h) => {
    if (h === 0) return '12a'
    if (h < 12) return `${h}a`
    if (h === 12) return '12p'
    return `${h - 12}p`
  }),
  datasets: (detail.value.hour_of_day_breakdown || []).map((series, i) => ({
    label: series.name,
    data: series.hour_counts,
    backgroundColor: series.name === 'Other'
      ? HOUR_BAR_COLORS[HOUR_BAR_COLORS.length - 1]
      : HOUR_BAR_COLORS[i % (HOUR_BAR_COLORS.length - 1)],
    stack: 'pages',
  })),
}))

const hourChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
    tooltip: { mode: 'index', intersect: false },
  },
  scales: {
    x: { stacked: true, grid: { display: false } },
    y: { stacked: true, beginAtZero: true, ticks: { precision: 0 }, grid: { color: 'rgba(0,0,0,0.05)' } },
  },
}

async function load() {
  const token = store.state.auth.token
  if (!token) {
    detailErrorMsg.value = 'Not authenticated.'
    loadingSummary.value = false
    loadingDetail.value = false
    return
  }
  try {
    summary.value = await getAdminDashboardData(token)
  } catch (e) {
    // non-fatal
  } finally {
    loadingSummary.value = false
  }
  try {
    detail.value = await getAdminDashboardAppUsageDetail(token)
  } catch (e) {
    const status = e && e.response && e.response.status
    detailErrorMsg.value = status ? `HTTP ${status}` : (e && e.message) || 'unknown error'
  } finally {
    loadingDetail.value = false
  }
}

load()
</script>
